import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Container, ColorBar, RegMark, SectionHead, QuoteForm, ContactButtons, Picture, FaqList,
  SERVICES, BRANCHES, INKS,
} from "@/components/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Content ─────────────────────────────────────────────────────────────── */
const steps = [
  { title: "Tell us what you need", body: "Call, WhatsApp, walk in or send the form. Artwork can be PDF, AI, PSD, CDR or a high-resolution image — or our designers can make it." },
  { title: "Approve a proof", body: "You get a digital proof before anything is printed. For large or critical runs we can make a physical sample first." },
  { title: "We print it", body: "Most standard jobs are ready 24–48 hours after you approve. Walk-in visiting cards can be done in about 15 minutes." },
  { title: "Collect or get it delivered", body: "Pick up from any branch, or we deliver across Bengaluru. Pan-India courier for everything else." },
];

const occasions = [
  {
    title: "Corporate gifting",
    body: "Branded diaries, mugs, T-shirts, pens and hampers, with your logo and individual names if you need them.",
    items: ["Employee kits", "Festive hampers", "Custom packaging"],
    image: "https://images.unsplash.com/photo-1720785004894-95a93636b58f?w=900&q=75",
    to: "/corporate-gifting",
  },
  {
    title: "Events and weddings",
    body: "Invitations on traditional or recycled stock, plus the banners, standees and signage for the day itself.",
    items: ["Wedding cards", "Event banners", "Standees"],
    image: "https://images.unsplash.com/photo-1430151451481-88f14fe27129?w=900&q=75",
    to: "/services/wedding-cards-printing-bangalore",
  },
  {
    title: "Office branding",
    body: "Sign boards, wall graphics, ID cards, letterheads and stationery that all match your brand colours.",
    items: ["Sign boards", "ID cards", "Stationery"],
    image: "https://images.unsplash.com/photo-1716703435900-8a3fa400aa30?w=900&q=75",
    to: "/services/office-stationery-printing-bangalore",
  },
];

const facts = [
  { figure: "2002", label: "Print Xpress opened its first shop in Koramangala" },
  { figure: "3", label: "branches across Bengaluru" },
  { figure: "24–48 h", label: "typical turnaround once you approve the proof" },
  { figure: "300", label: "A4 sheets a minute on our digital presses" },
];

const testimonials = [
  { name: "Rahul Mehta", role: "Founder, TechVentures", quote: "5,000 business cards in under 24 hours. Sharp colours, perfect finish. They've been our printer for three years." },
  { name: "Priya Sharma", role: "Marketing Manager, Urban Eats", quote: "The brochures for our restaurant launch were stunning, and the pricing was very reasonable." },
  { name: "Arjun Desai", role: "Event Coordinator", quote: "Needed large flex banners for a corporate event at short notice. Vibrant, durable, and delivered fast." },
  { name: "Sneha Patil", role: "Owner, Bloom Florals", quote: "Our custom stickers and packaging look amazing and hold up well. I keep coming back for everything." },
  { name: "Vikram Rao", role: "Director, BuildCraft Architects", quote: "Bill books printed exactly to spec, and the carbonless copies work perfectly. Great attention to detail." },
  { name: "Ananya Krishnan", role: "Startup Founder", quote: "From vinyl signage for the office to brochures for clients, they handle everything with precision." },
];

const faqs = [
  { q: "What is the minimum order?", a: "It depends on the product. Visiting cards start at 100, T-shirts and mugs at 10 pieces, and large-format prints like banners and standees can be a single piece." },
  { q: "How long does printing take?", a: "Most standard orders are ready 24–48 hours after you approve the design. Bulk or custom work can take 3–5 working days. Same-day express is available for an extra charge." },
  { q: "Can you design it for me?", a: "Yes. Our in-house designers create artwork for a small fee, with revisions until you're happy." },
  { q: "Which file formats do you accept?", a: "PDF, AI, PSD, CDR, PNG and JPEG. Vector files at 300 DPI in CMYK give the best results." },
  { q: "Will I see a proof first?", a: "Always. You approve a digital proof before the run starts. For large or critical orders we can print a physical sample for a small charge." },
  { q: "Do you deliver?", a: "Across Bengaluru, and free within Koramangala for orders over ₹1,000. We ship pan-India through courier partners." },
  { q: "Can you match our brand colours?", a: "Yes. We Pantone-match on offset work and calibrate our digital presses to your brand guidelines." },
  { q: "What if something's wrong with the print?", a: "If there's a defect or a mistake on our side, we reprint it at no charge." },
];

/* ── Sections ────────────────────────────────────────────────────────────── */
function Hero() {
  const line = <>We print<br />anything.</>;
  return (
    <section data-testid="hero-with-form" className="relative bg-paper pt-[4.5rem] overflow-hidden">
      <Container className="relative pt-12 sm:pt-16 lg:pt-20 pb-14 lg:pb-20">
        <RegMark className="absolute left-2 sm:left-4 top-4 text-ink/35" />
        <RegMark className="absolute right-2 sm:right-4 top-4 text-ink/35" />
        <div className="grid gap-12 lg:grid-cols-[1.25fr_1fr] lg:gap-14 items-start">
          <div className="pt-2">
            <h1 data-testid="hero-heading"
              className="font-display font-bold text-ink text-[clamp(3.6rem,16vw,6.5rem)] lg:text-[clamp(5rem,9.6vw,9.25rem)] leading-[0.86] tracking-[-0.05em]">
              <span className="overprint">
                <span className="plate plate-y" aria-hidden="true">{line}</span>
                <span className="plate plate-c" aria-hidden="true">{line}</span>
                <span className="plate plate-m" aria-hidden="true">{line}</span>
                <span className="plate-k">{line}</span>
              </span>
            </h1>
            <p className="mt-8 sm:mt-10 text-lg sm:text-xl leading-relaxed text-graphite max-w-xl">
              Visiting cards and wedding invites, T-shirts and flex banners, brochures and branded gift boxes.
              Printed on our own presses in Bengaluru since 2002, and most jobs are ready in 24 to 48 hours.
            </p>
            <ContactButtons className="mt-8" />
            <p className="mt-8 text-sm text-graphite">
              Walk in at Koramangala 7th Block, Koramangala 5th Block or Richmond Road.{" "}
              <a href="#branches" className="ink-link text-ink">Find a branch</a>
            </p>
          </div>
          <div id="quote" className="lg:mt-3 lg:shadow-[10px_10px_0_0_#161616]">
            <QuoteForm testid="hero-quote-form" title="Get a price" intro="Tell us what you need and we'll send a quote." />
          </div>
        </div>
      </Container>
      <ColorBar />
    </section>
  );
}

function Services() {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  return (
    <section id="services" data-testid="services-section" className="bg-paper py-20 md:py-28">
      <Container>
        <SectionHead
          title="What we print"
          lede="From a box of visiting cards to a building-sized flex banner. Prices below are starting points; send us the details for an exact quote."
        />

        {categories.length > 0 && (
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-16 md:mb-20">
            {categories.map((cat) => (
              <li key={cat.id}>
                <Link to={`/categories/${cat.id}`} data-testid={`category-card-public-${cat.id}`}
                  className="group flex h-full flex-col bg-stock border border-ink transition-[transform,box-shadow] duration-150 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#E5007D]">
                  <Picture src={cat.image} alt={cat.name} className="w-full aspect-[4/3] border-b border-ink" />
                  <div className="flex flex-1 flex-col p-5 sm:p-6">
                    <h3 className="font-display font-bold text-2xl text-ink">{cat.name}</h3>
                    {cat.description && <p className="mt-2 text-graphite leading-relaxed line-clamp-3">{cat.description}</p>}
                    <div className="mt-auto pt-6 flex items-end justify-between gap-4">
                      {cat.starting_price > 0
                        ? <p className="font-display text-ink"><span className="text-graphite text-sm block">From</span><span className="text-2xl font-bold">₹{cat.starting_price}</span></p>
                        : <span />}
                      <span className="inline-flex items-center gap-1 font-display font-semibold text-ink group-hover:text-process-magenta transition-colors">
                        See options <ArrowUpRight size={18} aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="border-t border-ink pt-8">
          <h3 className="font-display font-semibold text-graphite mb-6">Every service</h3>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8">
            {SERVICES.map((s, i) => (
              <li key={s.slug} className="border-b border-rule">
                <Link to={`/services/${s.slug}`}
                  className="group flex items-center gap-3 py-3.5 font-display font-medium text-lg text-ink hover:text-process-magenta transition-colors">
                  <span className="w-2.5 h-2.5 shrink-0" style={{ backgroundColor: INKS[i % 4] }} aria-hidden="true" />
                  <span className="flex-1">{s.name}</span>
                  <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

function Process() {
  return (
    <section data-testid="process-section" className="bg-process-yellow py-20 md:py-28 border-y border-ink">
      <Container>
        <SectionHead title="How an order works" lede="Four steps, and you see exactly what will print before it does." />
        <ol className="grid gap-px bg-ink border border-ink sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <li key={s.title} className="bg-process-yellow p-6 sm:p-8">
              <span className="font-display font-bold text-6xl leading-none text-ink" aria-hidden="true">{i + 1}</span>
              <h3 className="mt-6 font-display font-bold text-xl text-ink">{s.title}</h3>
              <p className="mt-3 text-ink/80 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </Container>
    </section>
  );
}

function Occasions() {
  return (
    <section id="corporate-gifting" data-testid="use-cases-section" className="bg-paper py-20 md:py-28">
      <Container>
        <SectionHead title="Print for the occasion" lede="Most of what we make is for one of these three. Tell us which, and we'll suggest what works." />
        <div className="grid gap-10 md:grid-cols-3 md:gap-8">
          {occasions.map((o) => (
            <article key={o.title} className="flex flex-col">
              <Picture src={o.image} alt={o.title} className="w-full aspect-[5/4] border border-ink" />
              <h3 className="mt-6 font-display font-bold text-2xl text-ink">{o.title}</h3>
              <p className="mt-3 text-graphite leading-relaxed">{o.body}</p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {o.items.map((it) => (
                  <li key={it} className="text-sm border border-ink rounded-full px-3 py-1 text-ink">{it}</li>
                ))}
              </ul>
              <Link to={o.to} className="mt-6 self-start ink-link font-display font-semibold text-ink">
                {o.title === "Corporate gifting" ? "Browse the gifting catalogue" : `See ${o.title.toLowerCase()} printing`}
              </Link>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}

function GiftingBand() {
  return (
    <section className="bg-process-magenta text-white">
      <Container className="py-16 md:py-20 grid gap-8 md:grid-cols-[1.4fr_1fr] md:items-end">
        <div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.035em]">
            Gifts for a team of 25, or 2,500.
          </h2>
          <p className="mt-5 text-lg text-white/90 max-w-xl leading-relaxed">
            Onboarding kits, Diwali hampers, conference bags. We brand them, pack them and ship them to one office or every desk in India.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 md:justify-end">
          <Link to="/corporate-gifting" className="btn btn-paper">Browse the catalogue</Link>
        </div>
      </Container>
    </section>
  );
}

function Facts() {
  return (
    <section data-testid="why-printxpress-section" className="bg-paper py-20 md:py-28">
      <Container>
        <div className="grid gap-12 lg:grid-cols-[1fr_1.6fr]">
          <div>
            <h2 className="font-display font-bold text-[2.25rem] sm:text-5xl text-ink">A local press, not a middleman</h2>
            <p className="mt-5 text-lg text-graphite leading-relaxed max-w-md">
              We started as a photocopy shop in the 1990s and have run our own presses ever since. That's why we can quote
              honestly, check every job before it leaves, and reprint for free if we get something wrong.
            </p>
            <Link to="/about" className="mt-6 inline-block ink-link font-display font-semibold text-ink">Read our story</Link>
          </div>
          <dl className="grid grid-cols-2 border-t border-l border-ink">
            {facts.map((f) => (
              <div key={f.figure} className="border-r border-b border-ink p-5 sm:p-8">
                <dt className="sr-only">{f.label}</dt>
                <dd>
                  <span className="block font-display font-bold text-4xl sm:text-6xl tracking-[-0.04em] text-ink">{f.figure}</span>
                  <span className="mt-3 block text-graphite leading-snug">{f.label}</span>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </Container>
    </section>
  );
}

function Testimonials() {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-stock border-y border-ink py-20 md:py-28">
      <Container>
        <SectionHead title="What customers say" />
        <ul className="grid gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((t, i) => (
            <li key={t.name} className="border-t-[3px] pt-6" style={{ borderColor: INKS[i % 4] }}>
              <blockquote>
                <p className="font-display text-xl leading-snug text-ink">“{t.quote}”</p>
                <footer className="mt-5 text-sm">
                  <span className="block font-semibold text-ink">{t.name}</span>
                  <span className="text-graphite">{t.role}</span>
                </footer>
              </blockquote>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}

function Branches() {
  return (
    <section id="branches" data-testid="locations-section" className="bg-ink text-paper py-20 md:py-28">
      <Container>
        <SectionHead dark title="Visit a branch" lede="Walk in with a file on your phone or a pen drive. We're open Monday to Saturday, 9 am to 8 pm." />
        <ul className="grid gap-px bg-paper/20 border border-paper/20 md:grid-cols-3">
          {BRANCHES.map((b, i) => (
            <li key={b.name} className="bg-ink p-6 sm:p-8 flex flex-col" data-testid={`location-card-${i}`}>
              <span className="w-8 h-8" style={{ backgroundColor: INKS[i] }} aria-hidden="true" />
              <h3 className="mt-6 font-display font-bold text-2xl">{b.name}</h3>
              <p className="mt-2 text-paper/70 leading-relaxed">{b.address}</p>
              <p className="mt-3 text-paper/90">{b.note}</p>
              <a href={b.map} target="_blank" rel="noopener noreferrer"
                className="mt-6 self-start inline-flex items-center gap-1 font-display font-semibold text-paper hover:text-process-yellow transition-colors">
                Get directions <ArrowUpRight size={16} aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}

function Faq() {
  return (
    <section data-testid="faq-section" className="bg-paper py-20 md:py-28">
      <Container className="grid gap-10 lg:grid-cols-[1fr_1.7fr]">
        <div className="lg:sticky lg:top-28 self-start">
          <h2 className="font-display font-bold text-[2.25rem] sm:text-5xl text-ink">Questions people ask</h2>
          <p className="mt-5 text-lg text-graphite leading-relaxed">Something else? Call or WhatsApp and you'll get a person, not a bot.</p>
          <ContactButtons className="mt-6" />
        </div>
        <FaqList faqs={faqs} />
      </Container>
    </section>
  );
}

export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-paper">
      <Navbar />
      <main>
        <Hero />
        <Services />
        <Process />
        <Occasions />
        <GiftingBand />
        <Facts />
        <Testimonials />
        <Faq />
        <Branches />
      </main>
      <Footer />
    </div>
  );
}
